/**
 * drop-menu.js
 */

import React from 'react';
import {
  useMediaQuery,
  List,
  MenuItem as MuiMenuItem,
  ListItemIcon as MuiListItemIcon,
  IconButton,
  Box,
  Divider,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from 'components/icon';
import Popover from 'components/popover';


const useStyles = makeStyles(theme => ({
  subMenu: {
    pointerEvents: 'none',
    '& .MuiPaper-root': {
      pointerEvents: 'all'
    }
  },
  menuItemDisabledEvents: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
    '&.Mui-disabled:hover': {
      textDecoration: 'inherit',
      backgroundColor: 'inherit',
    },
  },
  secondaryAction: {
    marginLeft: theme.spacing(2),
  },
  icon: {
    '&.MuiListItemIcon-root': {
      minWidth: theme.spacing(3),
    },
  },
  beforeIcon: {
    marginRight: props => props.iconMargin ? props.iconMargin : theme.spacing(1),
  },
  endIcon: {
    marginLeft: props => props.iconMargin ? props.iconMargin : theme.spacing(2),
  },
  active: {
    color: theme.palette.sys.primary,
  },
  overflowEllipses: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  activeText: {
    fontWeight: 700,
  },
}));


const ListItemIcon = (props) => {
  const { icon, active, isEndIcon } = props;
  const classes = useStyles();

  return (
    <MuiListItemIcon className={`${classes.icon} ${isEndIcon ? classes.endIcon : classes.beforeIcon}`}>
      <>
        {typeof icon === 'string' &&
        <Icon
          type={icon}
          className={active ? classes.active : null}
        />}

        {typeof icon === 'object' && icon}
      </>
    </MuiListItemIcon>
  )
}


const MenuItem = (props) => {
  const {
    id, 
    active, 
    name, 
    icon, 
    endIcon,
    secondaryActionIcon,
    onSecondaryAction,
    disabled, 
    tooltip, 
    onSelect, 
    component,
    overflowEllipses, 
    ...rest
  } = props;

  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  

  return (
    <Tooltip
      arrow
      title={tooltip ? tooltip : ''}
      placement='left'
    >
      <MuiMenuItem
        {...rest}
        component={disabled && tooltip ? 'div' : component}
        onClick={disabled ? null : onSelect}
        className={`${disabled && tooltip ? classes.menuItemDisabledEvents : null} ${active ? classes.active : null}`}
        disabled={disabled}
      >
        {icon && <ListItemIcon icon={icon} active={active} isEndIcon={false} />}
        <Box
          className={`${active ? classes.activeText : null} ${overflowEllipses ? classes.overflowEllipses : null}`}
          style={{width: '100%'}}
          children={name}
        />
        {endIcon && <ListItemIcon icon={endIcon} active={active} isEndIcon={true} />}
        {secondaryActionIcon && onSecondaryAction &&
          <div className={classes.secondaryAction}>
            <IconButton 
              edge='end' 
              size={isMobile ? undefined : 'small'}
              onClick={(e) => {e.stopPropagation(); onSecondaryAction(e)}}
            >
              {secondaryActionIcon}
            </IconButton>
          </div>        
        }
      </MuiMenuItem>
    </Tooltip>
  );
}


export default function DropMenu(props) {
  const {
    id,
    label,
    items,
    onSelect,
    anchorOrigin,
    transformOrigin,
    ...rest
  } = props;
  const classes = useStyles();


  const handleSelection = (item) => {
    if (!item.disabled && onSelect) {
      onSelect(item);
    }
  }


  return (
    <>
      <Popover
        id={id}
        data-testid={id}
        label={label}
        closeOnClick
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        {...rest}
      >
          <List>
          {items && items.map((item, i) => {
            return item.id === 'divider'
              ? <Divider key={`divider-${i}`} variant='middle' style={{ marginTop: 8, marginBottom: 8 }}  />
              : item.items
                ? <DropMenu
                  key={item.key || item.id || i}
                  className={classes.subMenu}
                  openEventName='onMouseEnter'
                  disableRestoreFocus
                  onSelect={item.onSelect}
                  items={item.items}
                  label={<MenuItem {...item} endIcon={<Icon type='arrow-down' />} key={item.key || item.id || i} />}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                /> 
                : (
                <MenuItem
                  {...item}
                  key={item.key || item.id || i}
                  data-testid={item['data-testid']}
                  onSelect={() => item.onSelect ? item.onSelect() : handleSelection(item)}
                />
              )
            })}
        </List>
      </Popover>
    </>
  );
}